<!-- Broadcast Message Popup Modal -->
<div *ngIf="showPopup && broadcastMessage && broadcastMessage.isVisible"
  class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
  <div class="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden animate-fade-in flex flex-col">
    <!-- Header -->
    <div class="flex items-center justify-between p-4 border-b bg-sp-green-500 border-sp-green-600 flex-shrink-0">
      <div class="flex items-center space-x-3">
        <!-- Icon based on type -->
        <svg *ngIf="broadcastMessage.type === 'info'" class="w-6 h-6 text-white" fill="none" stroke="currentColor"
          viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <svg *ngIf="broadcastMessage.type === 'warning'" class="w-6 h-6 text-white" fill="none" stroke="currentColor"
          viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z">
          </path>
        </svg>
        <svg *ngIf="broadcastMessage.type === 'error'" class="w-6 h-6 text-white" fill="none" stroke="currentColor"
          viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <h2 class="text-xl md:text-2xl font-bold text-white">{{ broadcastMessage.title }}</h2>
      </div>
      <button (click)="closePopup()" type="button"
        class="p-2 rounded-lg hover:bg-sp-green-600 transition-colors duration-200">
        <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    </div>

    <!-- Scrollable Content Area -->
    <div class="overflow-y-auto flex-1">
      <!-- Content -->
      <div class="p-4">
        <div class="space-y-4">
          <p *ngFor="let paragraph of broadcastMessage.content" class="text-gray-700 text-base leading-relaxed mb-2"
            [innerHTML]="paragraph"></p>
        </div>
      </div>

      <!-- Contact details -->
      <div class="p-4 border-t border-gray-200 bg-gray-50">
        <form [formGroup]="contactForm" class="space-y-4">
          <!-- First Name Field -->
          <div>
            <label for="firstName" class="block text-sm font-medium text-gray-700 mb-1">
              First Name
            </label>
            <input type="text" id="firstName" formControlName="firstName" placeholder="John"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sp-green-500 focus:border-transparent transition-all duration-200"
              [class.border-red-500]="contactForm.get('firstName')?.invalid && contactForm.get('firstName')?.touched" />
            <p *ngIf="getError('firstName')" class="mt-1 text-sm text-red-600">
              {{ getError('firstName') }}
            </p>
          </div>

          <!-- Email and Phone Number Fields Side by Side -->
          <div class="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-start">
            <!-- Email Field -->
            <div class="w-full">
              <label for="email" class="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input type="email" id="email" formControlName="email" placeholder="user@example.com"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sp-green-500 focus:border-transparent transition-all duration-200"
                [class.border-red-500]="contactForm.get('email')?.invalid && contactForm.get('email')?.touched" />
              <p *ngIf="getError('email')" class="mt-1 text-sm text-red-600">
                {{ getError('email') }}
              </p>
            </div>

            <!-- OR separator -->
            <div class="hidden md:flex items-center pt-8">
              <span class="text-gray-500 font-medium px-2">or</span>
            </div>

            <!-- Mobile OR separator -->
            <div class="md:hidden flex items-center justify-center">
              <span class="text-gray-500 font-medium">or</span>
            </div>

            <!-- Phone Number Field -->
            <div class="w-full">
              <label for="phoneNumber" class="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input type="tel" id="phoneNumber" formControlName="phoneNumber" placeholder="07123456789"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sp-green-500 focus:border-transparent transition-all duration-200"
                [class.border-red-500]="contactForm.get('phoneNumber')?.invalid && contactForm.get('phoneNumber')?.touched" />
              <p *ngIf="getError('phoneNumber')" class="mt-1 text-sm text-red-600">
                {{ getError('phoneNumber') }}
              </p>
            </div>
          </div>

          <!-- At Least One Contact Method Error -->
          <div *ngIf="showAtLeastOneContactError()" class="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p class="text-sm text-yellow-800">Please provide at least one contact method (Email or Phone Number).</p>
          </div>

          <!-- API Error Message -->
          <div *ngIf="apiError" class="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p class="text-sm text-red-600">{{ apiError }}</p>
          </div>
        </form>
      </div>
    </div>

    <!-- Footer - Fixed at Bottom -->
    <div class="flex items-center justify-end p-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
      <button (click)="closePopup()" type="button"
        class="px-6 py-2 mr-2 bg-gray-300 hover:bg-gray-400 text-gray-700 font-semibold rounded-lg transition-all duration-200 shadow-sm hover:shadow-md">
        Close
      </button>
      <button (click)="onNext()" type="button" [disabled]="isSubmitting"
        class="px-6 py-2 bg-sp-green-500 hover:bg-sp-green-600 text-white font-semibold rounded-lg transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed">
        {{ isSubmitting ? 'Processing...' : 'Next' }}
      </button>
    </div>
  </div>
</div>

<!-- Original Banner (Keeping for reference or alternative display) -->
<div class="bg-sp-yellow-400 text-sp-gray-900 py-2 md:py-3 px-4 shadow-sm border-b border-sp-yellow-500">
  <div class="max-w-7xl mx-auto flex items-center justify-center">
    <!-- Phone Icon and Message -->
    <div class="flex items-center space-x-3">
      <svg class="w-4 h-4 md:w-5 md:h-5 text-sp-gray-900 flex-shrink-0" fill="currentColor" viewBox="0 0 36 36"
        xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd"
          d="M28.845 22.89L25.035 22.455C24.12 22.35 23.22 22.665 22.575 23.31L19.815 26.07C15.57 23.91 12.09 20.445 9.93002 16.185L12.705 13.41C13.35 12.765 13.665 11.865 13.56 10.95L13.125 7.17001C12.945 5.65501 11.67 4.51501 10.14 4.51501H7.54502C5.85002 4.51501 4.44002 5.92501 4.54502 7.62001C5.34002 20.43 15.585 30.66 28.38 31.455C30.075 31.56 31.485 30.15 31.485 28.455V25.86C31.5 24.345 30.36 23.07 28.845 22.89Z" />
      </svg>
      <span class="text-sm sm:text-base md:text-lg font-medium">
        Prefer to talk to us about your business energy quote? Call us on
        <a href="tel:08006345511" class="font-semibold hover:underline text-sp-gray-900">0800 634 5511</a>.
      </span>
    </div>
  </div>
</div>